import React from "react";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";

export type GalleryTileProps = {
  title: string;
  subtitle: string;
  prompt?: string;
  tool?: string;
  onClick?: () => void;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  mode?: "light" | "dark";
  disableHoverAnimation?: boolean;
  phoneSubtitle?: string;
};

export const GalleryTile: React.FC<GalleryTileProps> = ({
  title,
  subtitle,
  onClick,
  width,
  height,
  style,
  mode = "light", // Default to light mode if not provided
  disableHoverAnimation = false,
}) => {
  const { isSmallLaptop } = useDeviceDetection();

  // Glass effect colors based on mode
  const isDark = mode === "dark";
  const glassGradient = isDark
    ? `rgba(66, 66, 72, 0.6)`
    : `linear-gradient(145deg, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.22) 30%, rgba(255,255,255,0.12) 70%, rgba(255,255,255,0.07) 100%)`;
  const glassBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(255,255,255,0.35)';
  const glassBoxShadow = isDark
    ? `0 8px 32px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.10)`
    : `0 8px 32px rgba(0,0,0,0.11), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.06)`;
  const glassReflection = isDark
    ? `transparent`
    : `linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 60%)`;
  const noiseBg = isDark
    ? `transparent`
    : `repeating-linear-gradient(0deg, #f5f5f5 0px, #f4f4f4 1px, #f5f5f5 2px), repeating-linear-gradient(90deg, #f5f5f5 0px, #f6f6f6 1px, #f5f5f5 2px)`;

  return (
    <div
      data-tile-height={height}
      data-gallery-tile=""
      className={`relative overflow-hidden rounded-xl cursor-pointer select-none group ${
        disableHoverAnimation ? '' : 'hover:scale-105 hover:-translate-y-2'
      }`}
      style={{
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined,
        minHeight: height ? `${height}px` : undefined,
        maxHeight: height ? `${height}px` : undefined,
        flexShrink: 0,
        flexGrow: 0,
        zIndex: 1,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, height 0.3s ease, min-height 0.3s ease, max-height 0.3s ease, z-index 0s',
        background: 'transparent',
        boxShadow: `0 2px 4px rgba(0,0,0,0.10), 
                   0 4px 8px rgba(0,0,0,0.06)`,
        ...style
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.zIndex = '100';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.zIndex = '1';
      }}
      onClick={onClick}
    >
      {/* Fine noise-like texture */}
      <div 
        className="absolute inset-0 rounded-xl"
        style={{
          background: noiseBg,
          backgroundBlendMode: 'multiply',
        }}
      />
      
      {/* Base gradient similar to active header button */}
      <div 
        className="absolute inset-0 rounded-xl"
        style={{
          background: isDark
            ? '#424248'
            : 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
        }}
      />
      
      {/* Glass effect */}
      <div 
        className="absolute inset-0 rounded-xl"
        style={{
          background: glassGradient,
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          border: glassBorder,
          boxShadow: glassBoxShadow,
        }}
      />
      
      {/* Glass reflection effect */}
      <div 
        className="absolute inset-0 rounded-xl"
        style={{
          background: glassReflection,
        }}
      />
      
      {/* Subtle glow effect on hover */}
      <div 
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300 z-0"
        style={{
          background: isDark
            ? `radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, transparent 70%)`
            : `radial-gradient(circle at center, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.25) 35%, rgba(255,255,255,0.08) 65%, transparent 80%)`,
          backdropFilter: isDark ? 'blur(0.5px)' : 'blur(1.3px)',
        }}
      />
      
      {/* Content container - single column layout */}
      <div className="relative z-10 w-full h-full flex flex-col justify-center px-4 py-1">
        <div 
          className="font-bold text-left my-1"
          style={{ 
            fontSize: isSmallLaptop ? 'clamp(0.59rem, 0.79em, 1.04rem)' : 'clamp(0.65rem, 0.85em, 1.1rem)',
            fontFamily: '3MCircularTT',
            color: 'var(--color-text)',
            textShadow: isDark
              ? '0 2px 6px rgba(0,0,0,0.45), 0 1px 0 rgba(0,0,0,0.18)'
              : '0 1px 2px rgba(0,0,0,0.08)',
            lineHeight: '1.2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        <div 
          className="text-left opacity-80" 
          style={{ 
            fontSize: isSmallLaptop ? 'clamp(0.54rem, 0.74em, 0.94rem)' : 'clamp(0.6rem, 0.8em, 1rem)',
            fontFamily: '3MCircularTT',
            color: 'var(--color-text)',
            textShadow: isDark
              ? '0 2px 6px rgba(0,0,0,0.45), 0 1px 0 rgba(0,0,0,0.18)'
              : '0 1px 2px rgba(0,0,0,0.08)',
            lineHeight: '1.3',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
};
